import React, { useEffect, useState } from 'react';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Plan as PlanV3, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { isPlanV3 } from '@wix/pricing-plans-utils';
import { useTranslation, useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { ButtonDemo } from '../../../../../constants/settings-events';
import { useAdaptedSettings, DynamicElementProvider } from '../../../../../hooks';
import { orderPlansForMobileView } from '../../../../../utils/orderPlansForMobileView';
import { getVariantDisplayFlags } from '../../../../../utils/plan-unified-utils';
import { getPlanTabCategoryIds, VariantCategoryNameById } from '../../../../../utils/tabs';
import { VariantsByPlan } from '../../../../../utils/variant-visibility';
import { Plan } from '../Plan';
import { classes } from './PlanList.st.css';

// Max item width + margins;
export const MAX_ITEM_WIDTH = 454 + 60;
export const DYNAMIC_HEIGHT_ELEMENTS = [
  { name: 'planName' },
  { name: 'tagline', minHeight: 40 },
  { name: 'setup-fee', minHeight: 40 },
];

export interface PlanListProps {
  plans: PublicPlan[] | PlanV3[];
  variantEntries?: VariantsByPlan;
  variantCategoryNameById?: VariantCategoryNameById;
  isVariantSelectionEnabled: boolean;
  isTabsView?: boolean;
  showDemoHighlight?: boolean;
  buttonDemo: ButtonDemo;
  expandMobileBenefits: boolean;
  selectPlan(plan: PublicPlan | PlanV3, variant: PricingVariant | undefined): void;
  addToCart(plan: PublicPlan | PlanV3, variant: PricingVariant | undefined): void;
  selectedPlanId?: string;
  isOnEcom: boolean;
  isAddToCartLoading?: boolean;
}

export const PlanList: React.FC<PlanListProps> = ({
  plans,
  variantEntries,
  variantCategoryNameById = {},
  isVariantSelectionEnabled,
  isTabsView = false,
  showDemoHighlight,
  buttonDemo,
  selectPlan,
  addToCart,
  expandMobileBenefits,
  selectedPlanId,
  isOnEcom,
  isAddToCartLoading,
}) => {
  const [isGoToCheckoutLoading, setIsGoToCheckoutLoading] = useState(false);
  const settings = useAdaptedSettings();
  const { experiments } = useExperiments();
  const { t } = useTranslation();
  const { isMobile, isEditorX } = useEnvironment();

  const highlightedPlanId = settings.getHighlightedPlanId(plans, showDemoHighlight);
  const highlightedVariant = settings.get('highlightedVariant');
  const orderedPlans = isMobile && highlightedPlanId ? orderPlansForMobileView(plans, highlightedPlanId) : plans;

  const getIsHighlighted = (plan: PublicPlan | PlanV3, variant: PricingVariant | undefined) => {
    if (plan.id !== highlightedPlanId) {
      return false;
    }

    if (!highlightedVariant) {
      // Tabs view highlights the plan across all of its variant tabs.
      if (isTabsView) {
        return true;
      }

      // Classic view defaults to highlighting the first variant only.
      const [firstTabCategoryId] = isPlanV3(plan) ? getPlanTabCategoryIds(plan, variantCategoryNameById) : [];

      return !firstTabCategoryId || !!variant?.categoryIds?.includes(firstTabCategoryId);
    }

    return !!variant?.categoryIds?.includes(highlightedVariant);
  };

  const entries: { key: string; plan: PublicPlan | PlanV3; variant: PricingVariant | undefined }[] =
    isVariantSelectionEnabled && variantEntries
      ? Object.entries(variantEntries).flatMap(([planId, variants]) => {
          const plan = orderedPlans.find((p) => p.id === planId);

          if (!plan) {
            return [];
          }

          return variants.map((variant) => ({ key: `${planId}-${variant.id}`, plan, variant }));
        })
      : orderedPlans.map((plan) => ({
          key: plan.id!,
          plan,
          variant: isPlanV3(plan) ? plan.pricingVariants![0] : undefined,
        }));

  const flags = getVariantDisplayFlags(
    plans,
    entries.flatMap((e) => (e.variant ? [e.variant] : [])),
    t,
  );
  const taglinesExist = entries.some(({ plan }) => !!plan.description);

  const maxWidth = MAX_ITEM_WIDTH * entries.length;

  useEffect(() => {
    if (experiments.enabled(TPA_EXPERIMENTS.IMPROVE_ECOM_LOADING_STATE) && isOnEcom) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout>;
    if (isGoToCheckoutLoading) {
      /* 
        Reset loading state automatically after 5 seconds
        in case checkout is on the same page as PlanList widget
        and component isn't destroyed on navigation
      */
      timeout = setTimeout(() => {
        setIsGoToCheckoutLoading(false);
      }, 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [experiments, isGoToCheckoutLoading, isOnEcom]);
  const onSelectPlan = (plan: PublicPlan | PlanV3, variant: PricingVariant | undefined) => {
    setIsGoToCheckoutLoading(true);
    selectPlan(plan, variant);
  };

  return (
    <DynamicElementProvider elements={DYNAMIC_HEIGHT_ELEMENTS}>
      <ul className={classes.container} style={isEditorX ? {} : { maxWidth }}>
        {entries.map(({ key, plan, variant }) => (
          <li key={key} data-hook={VIEWER_HOOKS.PLAN_LIST_ITEM}>
            <Plan
              buttonDemo={buttonDemo}
              expandMobileBenefits={expandMobileBenefits}
              anyBenefits={flags.anyBenefits}
              highlighted={getIsHighlighted(plan, variant)}
              plan={plan}
              variant={variant}
              recurringPlansExist={flags.recurringPlansExist}
              freeTrialDaysExist={flags.freeTrialDaysExist}
              validityCyclesExist={flags.validityCyclesExist}
              taglinesExist={taglinesExist}
              setupFeesExist={flags.setupFeesExist}
              onBuyNowClick={() => onSelectPlan(plan, variant)}
              onAddToCartClick={() => addToCart(plan, variant)}
              isGoToCheckoutLoading={isGoToCheckoutLoading && plan.id === selectedPlanId}
              isAddToCartLoading={isAddToCartLoading && plan.id === selectedPlanId}
              isOnEcom={isOnEcom}
            />
          </li>
        ))}
      </ul>
    </DynamicElementProvider>
  );
};
